import * as R from "remeda";
import { Identifier } from "react-admin";

type NodeIdentifier = Identifier;

type GraphVertex = {
	id: NodeIdentifier;
	depends_on: NodeIdentifier | null;
};

type TopologicalSortResult<T> = {
	immediate: T[];
	remaining: T[];
};

export function topologicalSort<Node extends GraphVertex>(
	nodes: Node[]
): TopologicalSortResult<Node> {
	// Breadth-first search the graph to order the nodes

	const nodeIds = new Set(R.map(nodes, R.prop("id")));

	const nodeDependencyExists = (node: Node) => nodeIds.has(node.depends_on!);

	const { rootNodes = [], dependentNodes = [] } = R.groupBy(
		nodes,
		(node: Node) =>
			nodeDependencyExists(node) ? "dependentNodes" : "rootNodes"
	);

	const nodeIdToDependentNodes = R.groupBy(
		dependentNodes,
		(node: Node) => node.depends_on!
	);

	const visitedNodes = new Set<Node>();

	const nodeQueue: Node[] = rootNodes;

	const sortingResult: TopologicalSortResult<Node> = {
		immediate: [],
		remaining: [],
	};

	while (nodeQueue.length > 0) {
		const currentNode = nodeQueue.pop()!;

		if (visitedNodes.has(currentNode)) {
			// a circuit breaker in case there is a circular dependency - should never happen
			continue;
		} else {
			visitedNodes.add(currentNode);
		}

		if (nodeDependencyExists(currentNode)) {
			sortingResult.remaining.push(currentNode);
		} else {
			sortingResult.immediate.push(currentNode);
		}

		const nodesDependentOnCurrentNode = nodeIdToDependentNodes[currentNode.id];

		if (nodesDependentOnCurrentNode) {
			nodeQueue.unshift(...nodesDependentOnCurrentNode);
		}
	}

	return sortingResult;
}
